import { useEffect } from "react";
import { useAtomValue, useAtom } from "jotai";
import { createFileRoute } from "@tanstack/react-router";
import { extractDateFromDate } from "@/utilities/dateFormatter";
import { useRoutines } from "@/hooks/routines/useRoutines";
import { DashboardProgressCard } from "@/components/dashboard/DashboardCircle/DailyProgressCard";
import { useMobile } from "@/providers/mobileProvider";
import { useUser } from "@/hooks/useUser";
import { RoutinesToggleGroup } from "@/components/dashboard/RoutinesToggleGroup";
import { useMobileNavbar } from "@/providers/mobileNavbarProvider";
import { FilteredRoutines } from "@/components/dashboard/FilteredRoutines";
import { hasUpcomingRoutines, hasCompletedRoutines } from "@/hooks/routines/useRoutine.utils";
import { getGreeting } from "@/utilities/getGreeting";
import { NotFound } from "@/not-found";
import { GlobalError } from "@/global-error";
import {
  routineFilterModeAtom,
  timePeriodModeAtom,
  dashboardActiveDateAtom,
} from "@/state/dashboardPreferences";
import { Container } from "@/components/ui/container";
import { useTranslation } from "@/hooks/useTranslation";
import { ShareProgress } from "@/components/progress/ShareProgress";
import { Heading } from "@/components/ui/heading";

export const Route = createFileRoute("/dashboard/")({
  component: DashboardPage,
  notFoundComponent: NotFound,
  errorComponent: GlobalError,
});

function DashboardPage() {
  const { isMobile } = useMobile();
  const { routines, isLoading } = useRoutines();
  const { user } = useUser();
  const [activeDate, setActiveDate] = useAtom(dashboardActiveDateAtom);
  const activeDateString = extractDateFromDate(activeDate);
  const { setMobileNavbar } = useMobileNavbar();
  const filterMode = useAtomValue(routineFilterModeAtom);
  const timePeriodMode = useAtomValue(timePeriodModeAtom);
  const { t } = useTranslation();

  const _hasUpcomingRoutines = hasUpcomingRoutines(routines);
  const _hasCompletedRoutines = hasCompletedRoutines(routines);

  // Set up navbar configuration
  useEffect(() => {
    if (isMobile) {
      const greeting = getGreeting(new Date(), t);
      const userName = user?.name ? user.name.split(" ")[0] : "";
      const navbarTitle = `👋 ${greeting}, ${userName}`;

      setMobileNavbar({
        title: navbarTitle,
        navbarChildren: (
          <div className="flex items-center gap-2">
            {(_hasUpcomingRoutines || _hasCompletedRoutines) && (
              <RoutinesToggleGroup
                className="w-32"
                hasUpcomingRoutines={_hasUpcomingRoutines}
                hasCompletedRoutines={_hasCompletedRoutines}
              />
            )}
          </div>
        ),
      });
    }
    // @ts-expect error do not add t here as it will cause infinite loop
  }, [isMobile, user, _hasUpcomingRoutines, _hasCompletedRoutines, setMobileNavbar]);

  if (isLoading) return null;

  return (
    <Container>
      {!isMobile && (
        <div className="col-span-12 flex items-center justify-between space-x-2 @[1024px]:col-start-1">
          <RoutinesToggleGroup
            hasUpcomingRoutines={hasUpcomingRoutines(routines)}
            hasCompletedRoutines={hasCompletedRoutines(routines)}
          />
        </div>
      )}

      {filterMode === "active" && (
        <DashboardProgressCard activeDate={activeDateString} routines={routines} />
      )}

      {filterMode === "active" && timePeriodMode === "today" && (
        <div className="col-span-12 flex items-center justify-between space-x-2 @[1024px]:col-start-1">
          <Heading level="h2" size="h4">
            {t("dashboard.routines_for_today")}
          </Heading>
        </div>
      )}

      {timePeriodMode === "today" && (
        <FilteredRoutines
          routines={routines}
          hasUpcomingRoutines={_hasUpcomingRoutines}
          hasCompletedRoutines={_hasCompletedRoutines}
          activeDate={activeDateString}
        />
      )}

      {timePeriodMode === "week" && (
        <ShareProgress routines={routines} currentDate={activeDate} onDateChange={setActiveDate} />
      )}
    </Container>
  );
}
